import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { Send, X } from 'lucide-react';
import { emailConfigService, sendEmailService, emailTemplatesService } from '../services/emailService';
import TemplateSelector from './TemplateSelector';
import EntitySelector from './EntitySelector';
import VariableDisplay from './VariableDisplay';
import './Email.css';
import {
  validateFilesForEmail,
  filesToEmailAttachmentPayloads,
  EMAIL_ATTACHMENT_MAX_FILES,
  EMAIL_ATTACHMENT_MAX_TOTAL_BYTES
} from '../utils/emailClientAttachments';

const SendEmailForm = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    configurationId: '',
    receiver: '',
    cc: '',
    bcc: '',
    subject: '',
    body: ''
  });
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [entityIds, setEntityIds] = useState({});
  const [resolvedVariables, setResolvedVariables] = useState({});
  const [resolvingVariables, setResolvingVariables] = useState(false);
  
  // Receiver autocomplete state
  const [receiverQuery, setReceiverQuery] = useState('');
  const [receiverSuggestions, setReceiverSuggestions] = useState([]);
  const [showReceiverSuggestions, setShowReceiverSuggestions] = useState(false);
  const receiverSuggestionsRef = useRef(null);
  const receiverInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const [emailAttachments, setEmailAttachments] = useState([]);

  // Load configurations
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        const response = await emailConfigService.getAllConfigurations();
        if (response.status && response.data) {
          const activeConfigs = response.data.filter(c => c.isActive);
          setConfigurations(activeConfigs);
          
          // Set default configuration if available
          const defaultConfig = activeConfigs.find(c => c.isDefault);
          if (defaultConfig) {
            setFormData(prev => ({ ...prev, configurationId: defaultConfig.id.toString() }));
          }
        }
      } catch (err) {
        console.error('Failed to load configurations:', err);
      }
    };
    loadConfigurations();
  }, []);

  // Check if template was pre-selected from EmailTemplates page
  useEffect(() => {
    if (location.state?.selectedTemplate) {
      setSelectedTemplate(location.state.selectedTemplate);
    }
  }, [location.state]);

  // Load template data when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      const loadTemplate = async () => {
        try {
          const response = await emailTemplatesService.getTemplateByCode(selectedTemplate);
          if (response.status && response.data) {
            setTemplateData(response.data);
          }
        } catch (err) {
          console.error('Failed to load template:', err);
          setError(err.message);
        }
      };
      loadTemplate();
    } else {
      setTemplateData(null);
      setEntityIds({});
      setResolvedVariables({});
    }
  }, [selectedTemplate]);

  // Resolve variables when entities change
  useEffect(() => {
    if (selectedTemplate && templateData) {
      const resolveVariables = async () => {
        // Check if required entity is selected
        if (templateData.requiredEntity) {
          const requiredId = entityIds[`${templateData.requiredEntity}Id`];
          if (!requiredId) {
            setResolvedVariables({});
            setResolvingVariables(false);
            setError(null); // Clear error when entity not selected yet
            return;
          }
        }

        try {
          setResolvingVariables(true);
          setError(null); // Clear previous errors
          
          console.log('Resolving variables for template:', selectedTemplate, 'with entityIds:', entityIds);
          
          const response = await emailTemplatesService.resolveTemplateVariables(
            selectedTemplate,
            entityIds
          );

          console.log('Variable resolution response:', response);

          if (response.status && response.data) {
            const variables = response.data.variables || {};
            setResolvedVariables(variables);
            
            // Auto-populate subject and body only if we have variables (more than just system vars)
            const systemVars = ['currentDate', 'currentTime', 'currentYear', 'senderName', 'senderEmail'];
            const hasEntityVars = Object.keys(variables).some(key => !systemVars.includes(key));
            
            if (hasEntityVars && Object.keys(variables).length > systemVars.length) {
              try {
                const previewResponse = await emailTemplatesService.getTemplatePreview(
                  selectedTemplate,
                  entityIds
                );

                if (previewResponse.status && previewResponse.data) {
                  setFormData(prev => ({
                    ...prev,
                    subject: previewResponse.data.subject || prev.subject,
                    body: previewResponse.data.bodyHtml || prev.body
                  }));
                }
              } catch (previewErr) {
                console.error('Failed to get preview (non-critical):', previewErr);
                // Don't show error for preview failure
              }
            }
          } else {
            console.warn('Unexpected response format:', response);
          }
        } catch (err) {
          console.error('Failed to resolve variables:', err);
          // Only show error if it's a real error (not just missing entity)
          const errorMessage = err.message || 'Failed to resolve template variables';
          // Don't show error if user is still selecting entities
          if (errorMessage.includes('not found') || 
              errorMessage.includes('Required entity') ||
              errorMessage.includes('Template with code')) {
            // These are expected errors when entities aren't selected yet
            setError(null);
          } else {
            // Show actual errors
            setError(errorMessage);
          }
          setResolvedVariables({});
        } finally {
          setResolvingVariables(false);
        }
      };

      // Debounce variable resolution
      const timer = setTimeout(resolveVariables, 500);
      return () => clearTimeout(timer);
    } else {
      // Clear resolved variables when template is deselected
      setResolvedVariables({});
      setResolvingVariables(false);
      setError(null);
    }
  }, [selectedTemplate, templateData, entityIds]);

  // Search users for receiver autocomplete
  useEffect(() => {
    const searchUsers = async () => {
      // Get the last part after comma (for multiple recipients)
      const lastPart = receiverQuery.split(',').pop().trim();
      
      if (lastPart.length < 2) {
        setReceiverSuggestions([]);
        setShowReceiverSuggestions(false);
        return;
      }

      try {
        const response = await sendEmailService.searchUsers(lastPart);
        if (response.status && response.data) {
          setReceiverSuggestions(response.data);
          setShowReceiverSuggestions(true);
        }
      } catch (err) {
        console.error('Failed to search users:', err);
        setReceiverSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [receiverQuery]);

  // Handle click outside to close receiver suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        receiverSuggestionsRef.current &&
        !receiverSuggestionsRef.current.contains(event.target) &&
        receiverInputRef.current &&
        !receiverInputRef.current.contains(event.target)
      ) {
        setShowReceiverSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleReceiverChange = (e) => {
    const value = e.target.value;
    setReceiverQuery(value);
    setFormData(prev => ({ ...prev, receiver: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSelectReceiver = (user) => {
    // Get existing recipients (before the last comma)
    const existingRecipients = receiverQuery.split(',').slice(0, -1).join(',').trim();
    const newReceiver = existingRecipients 
      ? `${existingRecipients}, ${user.email}`
      : user.email;
    
    setFormData(prev => ({ ...prev, receiver: newReceiver }));
    setReceiverQuery(newReceiver);
    setShowReceiverSuggestions(false);
    
    // Focus back on input for adding more recipients
    setTimeout(() => {
      if (receiverInputRef.current) {
        receiverInputRef.current.focus();
        // Move cursor to end
        const length = receiverInputRef.current.value.length;
        receiverInputRef.current.setSelectionRange(length, length);
      }
    }, 0);
  };

  const handleTemplateChange = (templateCode) => {
    setSelectedTemplate(templateCode);
    setEntityIds({});
    setResolvedVariables({});
    setFormData(prev => ({
      ...prev,
      subject: '',
      body: ''
    }));
  };

  const handleEntityChange = (entityType, value) => {
    setEntityIds(prev => {
      const newIds = { ...prev };
      if (value) {
        newIds[`${entityType}Id`] = value;
      } else {
        delete newIds[`${entityType}Id`];
        // Clear dependent entities
        if (entityType === 'company') {
          delete newIds.projectId;
          delete newIds.releaseId;
          delete newIds.loadId;
          delete newIds.quoteId;
        } else if (entityType === 'project') {
          delete newIds.releaseId;
          delete newIds.loadId;
          delete newIds.quoteId;
        }
      }
      return newIds;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      if (!formData.receiver || !formData.subject || !formData.body) {
        throw new Error('Please fill in all required fields: Receiver, Subject, and Body');
      }

      // If using template, validate required entities
      if (selectedTemplate && templateData) {
        if (templateData.requiredEntity) {
          const requiredId = entityIds[`${templateData.requiredEntity}Id`];
          if (!requiredId) {
            throw new Error(`Please select a ${templateData.requiredEntityLabel || templateData.requiredEntity}`);
          }
        }
      }

      let attachmentsPayload;
      if (emailAttachments.length > 0) {
        attachmentsPayload = await filesToEmailAttachmentPayloads(emailAttachments);
      }

      let response;
      if (selectedTemplate) {
        // Send email with template
        const emailData = {
          templateCode: selectedTemplate,
          entityIds: entityIds,
          configurationId: formData.configurationId || null,
          receiver: formData.receiver,
          cc: formData.cc || undefined,
          bcc: formData.bcc || undefined,
          ...(attachmentsPayload?.length ? { attachments: attachmentsPayload } : {})
        };
        response = await sendEmailService.sendEmailWithTemplate(emailData);
      } else {
        // Send regular email
        const emailData = {
          configurationId: formData.configurationId || null,
          receiver: formData.receiver,
          cc: formData.cc || undefined,
          bcc: formData.bcc || undefined,
          subject: formData.subject,
          body: formData.body,
          bodyType: 'html',
          ...(attachmentsPayload?.length ? { attachments: attachmentsPayload } : {})
        };
        response = await sendEmailService.sendEmail(emailData);
      }

      if (response.status) {
        setSuccess('Email sent successfully!');
        // Reset form
        setFormData({
          configurationId: formData.configurationId, // Keep selected config
          receiver: '',
          cc: '',
          bcc: '',
          subject: '',
          body: ''
        });
        setReceiverQuery('');
        setReceiverSuggestions([]);
        setShowReceiverSuggestions(false);
        setEmailAttachments([]);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="send-email-card">
      <Card.Header>
        <h5 className="mb-0">Send Email</h5>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Template Selector */}
          <TemplateSelector
            value={selectedTemplate}
            onChange={handleTemplateChange}
            disabled={sending}
          />

          {/* Entity Selectors - Show when template is selected */}
          {selectedTemplate && templateData && (
            <Card className="mb-3" style={{ backgroundColor: '#f8f9fa' }}>
              <Card.Header>
                <strong>Select Entities</strong>
              </Card.Header>
              <Card.Body>
                {/* Always show Company first if it's in required or optional entities */}
                {(templateData.requiredEntity === 'company' || 
                  (templateData.optionalEntities && templateData.optionalEntities.includes('company'))) && (
                  <EntitySelector
                    entityType="company"
                    label={templateData.requiredEntity === 'company' 
                      ? (templateData.requiredEntityLabel || 'Select Company')
                      : 'Select Company'}
                    value={entityIds.companyId}
                    onChange={(value) => handleEntityChange('company', value)}
                    required={templateData.requiredEntity === 'company'}
                    disabled={sending || resolvingVariables}
                  />
                )}

                {/* Then show Project if it's in required or optional entities */}
                {(templateData.requiredEntity === 'project' || 
                  (templateData.optionalEntities && templateData.optionalEntities.includes('project'))) && (
                  <EntitySelector
                    entityType="project"
                    label={templateData.requiredEntity === 'project' 
                      ? (templateData.requiredEntityLabel || 'Select Project')
                      : 'Select Project'}
                    value={entityIds.projectId}
                    onChange={(value) => handleEntityChange('project', value)}
                    required={templateData.requiredEntity === 'project'}
                    filterBy={entityIds.companyId ? { companyId: entityIds.companyId } : null}
                    disabled={sending || resolvingVariables || !entityIds.companyId}
                  />
                )}

                {/* Then show other required entities (release, load, quote) */}
                {templateData.requiredEntity && 
                 templateData.requiredEntity !== 'company' && 
                 templateData.requiredEntity !== 'project' && (
                  <EntitySelector
                    entityType={templateData.requiredEntity}
                    label={templateData.requiredEntityLabel || `Select ${templateData.requiredEntity}`}
                    value={entityIds[`${templateData.requiredEntity}Id`]}
                    onChange={(value) => handleEntityChange(templateData.requiredEntity, value)}
                    required={true}
                    filterBy={
                      templateData.requiredEntity === 'release' && entityIds.projectId
                        ? { projectId: entityIds.projectId }
                        : templateData.requiredEntity === 'load' && entityIds.projectId
                        ? { projectId: entityIds.projectId }
                        : templateData.requiredEntity === 'quote' && entityIds.projectId
                        ? { projectId: entityIds.projectId }
                        : null
                    }
                    disabled={sending || resolvingVariables || !entityIds.projectId}
                  />
                )}

                {/* Then show other optional entities (excluding company and project which are already shown) */}
                {templateData.optionalEntities && templateData.optionalEntities.length > 0 && (
                  <>
                    {templateData.optionalEntities
                      .filter(entityType => entityType !== 'company' && entityType !== 'project')
                      .map(entityType => (
                        <EntitySelector
                          key={entityType}
                          entityType={entityType}
                          label={`Select ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`}
                          value={entityIds[`${entityType}Id`]}
                          onChange={(value) => handleEntityChange(entityType, value)}
                          required={false}
                          filterBy={
                            entityType === 'release' && entityIds.projectId
                              ? { projectId: entityIds.projectId }
                              : entityType === 'load' && entityIds.projectId
                              ? { projectId: entityIds.projectId }
                              : entityType === 'quote' && entityIds.projectId
                              ? { projectId: entityIds.projectId }
                              : null
                          }
                          disabled={sending || resolvingVariables || (entityType !== 'company' && !entityIds.projectId)}
                        />
                      ))}
                  </>
                )}

                {resolvingVariables && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Spinner size="sm" />
                    <span className="text-muted">Resolving variables...</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Variable Display */}
          {selectedTemplate && Object.keys(resolvedVariables).length > 0 && (
            <VariableDisplay
              variables={resolvedVariables}
              template={templateData}
            />
          )}

          {/* Configuration Dropdown */}
          <Form.Group className="mb-3">
            <Form.Label>Email Configuration</Form.Label>
            <Form.Select
              name="configurationId"
              value={formData.configurationId}
              onChange={handleChange}
            >
              <option value="">Use Default Configuration</option>
              {configurations.map(config => (
                <option key={config.id} value={config.id}>
                  {config.name} {config.isDefault && '(Default)'} - {config.fromEmail}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Select a configuration or leave blank to use the default. The email will be sent from the selected configuration's email address.
            </Form.Text>
          </Form.Group>

          {/* Receiver with Autocomplete */}
          <Form.Group className="mb-3">
            <Form.Label>Receiver (To) *</Form.Label>
            <div className="position-relative">
              <Form.Control
                ref={receiverInputRef}
                type="text"
                name="receiver"
                value={receiverQuery || formData.receiver}
                onChange={handleReceiverChange}
                placeholder="Type name or email to search users, or enter email manually (comma-separated for multiple)"
                required
                autoComplete="off"
              />
              {showReceiverSuggestions && receiverSuggestions.length > 0 && (
                <div ref={receiverSuggestionsRef} className="autocomplete-suggestions">
                  {receiverSuggestions.map((user, index) => (
                    <div
                      key={user.id || index}
                      className="suggestion-item"
                      onClick={() => handleSelectReceiver(user)}
                    >
                      <div className="suggestion-name">{user.display || user.name}</div>
                      {user.email && user.display && (
                        <div className="suggestion-email">{user.email}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Form.Text className="text-muted">
              Start typing to search users by name or email. You can add multiple recipients separated by commas.
            </Form.Text>
          </Form.Group>

          {/* CC */}
          <Form.Group className="mb-3">
            <Form.Label>CC (Carbon Copy)</Form.Label>
            <Form.Control
              type="text"
              name="cc"
              value={formData.cc}
              onChange={handleChange}
              placeholder="email@example.com (comma-separated for multiple)"
            />
            <Form.Text className="text-muted">
              Optional: Enter email address(es) for CC, separated by commas
            </Form.Text>
          </Form.Group>

          {/* BCC */}
          <Form.Group className="mb-3">
            <Form.Label>BCC (Blind Carbon Copy)</Form.Label>
            <Form.Control
              type="text"
              name="bcc"
              value={formData.bcc}
              onChange={handleChange}
              placeholder="email@example.com (comma-separated for multiple)"
            />
            <Form.Text className="text-muted">
              Optional: Enter email address(es) for BCC, separated by commas
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Attachments</Form.Label>
            <input
              ref={attachmentInputRef}
              type="file"
              className="d-none"
              multiple
              onChange={(e) => {
                const picked = e.target.files;
                if (!picked?.length) return;
                const merged = [...emailAttachments, ...Array.from(picked)];
                const v = validateFilesForEmail(merged);
                if (!v.ok) {
                  setError(v.error);
                  e.target.value = '';
                  return;
                }
                setEmailAttachments(v.files);
                setError(null);
                e.target.value = '';
              }}
            />
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={sending}
                onClick={() => attachmentInputRef.current?.click()}
              >
                Add files
              </Button>
              <Form.Text className="text-muted mb-0">
                Up to {EMAIL_ATTACHMENT_MAX_FILES} files, {EMAIL_ATTACHMENT_MAX_TOTAL_BYTES / (1024 * 1024)} MB total
              </Form.Text>
            </div>
            {emailAttachments.length > 0 && (
              <ul className="list-unstyled small mb-0 border rounded p-2 bg-light">
                {emailAttachments.map((file, idx) => (
                  <li key={`${file.name}-${idx}`} className="d-flex justify-content-between align-items-center py-1">
                    <span className="text-truncate me-2" title={file.name}>
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="text-danger p-0 flex-shrink-0"
                      disabled={sending}
                      onClick={() => setEmailAttachments((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Form.Group>

          {/* Subject */}
          <Form.Group className="mb-3">
            <Form.Label>Subject *</Form.Label>
            <Form.Control
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="Email subject"
              required
              disabled={resolvingVariables}
            />
            {selectedTemplate && (
              <Form.Text className="text-muted">
                Subject is auto-populated from template. You can edit it if needed.
              </Form.Text>
            )}
          </Form.Group>

          {/* Body */}
          <Form.Group className="mb-3">
            <Form.Label>Body (Message) *</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              name="body"
              value={formData.body}
              onChange={handleChange}
              placeholder="Enter your email message here..."
              required
              disabled={resolvingVariables}
            />
            <Form.Text className="text-muted">
              {selectedTemplate 
                ? 'Body is auto-populated from template. You can edit it if needed. HTML is supported.'
                : 'HTML is supported in the email body'}
            </Form.Text>
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setFormData({
                  configurationId: formData.configurationId,
                  receiver: '',
                  cc: '',
                  bcc: '',
                  subject: '',
                  body: ''
                });
                setReceiverQuery('');
                setReceiverSuggestions([]);
                setShowReceiverSuggestions(false);
                setEmailAttachments([]);
                setError(null);
                setSuccess(null);
              }}
              disabled={sending}
            >
              Clear
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={sending}
              className="d-flex align-items-center gap-2"
            >
              {sending ? (
                <>
                  <Spinner size="sm" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default SendEmailForm;

